// Game Schedule (the Quiz): the pure logic behind the page and the Schedule Game popup — period resolution,
// the form model and its conversion from/to the API, validation (plan 9.2.4), overlap checks, date formatting
// and the phase of a quiz. No React and no I/O, so every rule is unit-tested on its own.
import { addDays } from './dateUtils';
import {
  DEFAULT_CUSTOM_PERIOD_DAYS, DEFAULT_OPTION_COUNT, DEFAULT_QUESTION_COUNT, FRIDAY, MIN_OPTIONS, NAME_MAX_LENGTH,
} from '../constants/gameSchedule';

const pad2 = (n) => String(n).padStart(2, '0');

/** "2026-10-23T00:00:00" / "2026-10-23" / Date-only values from the API -> "2026-10-23" ('' when empty). */
export const isoDate = (value) => (value ? String(value).slice(0, 10) : '');

const dayOfWeek = (iso) => new Date(`${iso}T00:00:00Z`).getUTCDay(); // 0 = Sunday .. 6 = Saturday

// ── Period resolution (mirrors the server; plan 7.1) ─────────────────────────────────────────────────────────

/**
 * The period a Weekly / Monthly quiz covers for a reference date: the Monday-to-Sunday week, or the calendar month,
 * containing it. The server computes the same thing from the Eastern calendar date the marketer picked, so this
 * is only what the form shows; Custom has no resolution (the dates are exactly the two that were picked).
 */
export const resolvePeriod = (type, referenceDate) => {
  const ref = isoDate(referenceDate);
  if (!ref) return null;

  if (type === 'weekly') {
    const sinceMonday = (dayOfWeek(ref) + 6) % 7;
    const start = addDays(ref, -sinceMonday);
    return { start, end: addDays(start, 6) };
  }

  if (type === 'monthly') {
    const [year, month] = ref.split('-').map(Number);
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return { start: `${year}-${pad2(month)}-01`, end: `${year}-${pad2(month)}-${pad2(lastDay)}` };
  }

  return null;
};

// ── Overlap (the server enforces it too: 409 quiz_date_overlap) ──────────────────────────────────────────────

// Two inclusive date ranges overlap iff each one starts on or before the other ends.
export const rangesOverlap = (aStart, aEnd, bStart, bEnd) => aStart <= bEnd && aEnd >= bStart;

/** The other quizzes of the space as { id, name, startStr, endStr }, without the one being edited. */
export const toSiblingRanges = (quizzes, selfId = null) =>
  (Array.isArray(quizzes) ? quizzes : [])
    .filter((q) => q && q._viewType !== 'snapshot' && q.id !== selfId && q.startDate && q.endDate)
    .map((q) => ({ id: q.id, name: q.name, startStr: isoDate(q.startDate), endStr: isoDate(q.endDate) }));

export const findOverlap = (start, end, siblings) => {
  if (!start || !end || start > end) return null;
  return (siblings ?? []).find((s) => rangesOverlap(start, end, s.startStr, s.endStr)) ?? null;
};

// The first Friday on or after a date.
const fridayOnOrAfter = (iso) => addDays(iso, (FRIDAY - dayOfWeek(iso) + 7) % 7);

/**
 * Default dates of a new Custom period: the next free Friday (today counts) through the following Thursday. When the
 * week is taken it moves on to the first Friday after the quiz that is in the way, so the form opens on a period
 * that is valid.
 */
export const nextFreeCustomPeriod = (siblings, today) => {
  let start = fridayOnOrAfter(today);
  for (let guard = 0; guard < 520; guard += 1) {
    const end = addDays(start, DEFAULT_CUSTOM_PERIOD_DAYS - 1);
    const clash = findOverlap(start, end, siblings);
    if (!clash) return { start, end };
    start = fridayOnOrAfter(addDays(clash.endStr, 1));
  }
  const end = addDays(start, DEFAULT_CUSTOM_PERIOD_DAYS - 1);
  return { start, end };
};

/**
 * The date a new Weekly / Monthly quiz opens on: `from` itself while its week / month is free, otherwise the first day of
 * the first free week / month after the quiz that is in the way, so the form opens on a period that is valid.
 */
export const nextFreeReferenceDate = (type, siblings, from) => {
  let ref = from;
  for (let guard = 0; guard < 520; guard += 1) {
    const period = resolvePeriod(type, ref);
    const clash = period ? findOverlap(period.start, period.end, siblings) : null;
    if (!clash) return ref;
    // The period that follows the one the clashing quiz ends in (always later than the quiz, so the loop moves on).
    ref = addDays(resolvePeriod(type, clash.endStr).end, 1);
  }
  return ref;
};

// ── Quiz announcements (one announcement holds 1..N quizzes) ─────────────────────────────────────────────────

/** The quizzes an announcement is for: its list, else the legacy single quiz of an older row, else none (Penalty). */
export const announcementQuizzes = (announcement) => {
  const list = announcement?.gameSchedules;
  if (Array.isArray(list) && list.length > 0) return list;
  if (!announcement?.gameScheduleId && !announcement?.gameScheduleName) return [];
  return [{
    id: announcement.gameScheduleId ?? null,
    name: announcement.gameScheduleName || `Quiz #${announcement.gameScheduleId}`,
    startDate: announcement.startDate,
    endDate: announcement.endDate,
  }];
};

export const isQuizAnnouncement = (announcement) => announcementQuizzes(announcement).length > 0;

/** The earliest start and the latest end of some quizzes (ISO dates), or null when there are none with dates. */
export const quizSpan = (quizzes) => {
  const starts = (quizzes ?? []).map((q) => isoDate(q?.startDate)).filter(Boolean).sort();
  const ends = (quizzes ?? []).map((q) => isoDate(q?.endDate)).filter(Boolean).sort();
  return starts.length && ends.length ? { start: starts[0], end: ends[ends.length - 1] } : null;
};

/**
 * The dates a linked announcement gets when its quiz moves to `period` ({ start, end }): the earliest start and the
 * latest end across this quiz and the announcement's other quizzes (the API sends their span as otherStartDate /
 * otherEndDate, null when this is its only quiz). `changed` says whether they differ from the saved ones: only then
 * does the announcement's prize day have to be asked for again. Same formula as the server.
 */
export const recomputeLinkedAnnouncement = (period, linked) => {
  if (!period || !linked) return null;
  const otherStart = isoDate(linked.otherStartDate);
  const otherEnd = isoDate(linked.otherEndDate);
  const start = otherStart && otherStart < period.start ? otherStart : period.start;
  const end = otherEnd && otherEnd > period.end ? otherEnd : period.end;
  return { start, end, changed: start !== isoDate(linked.startDate) || end !== isoDate(linked.endDate) };
};

// ── Formatting ───────────────────────────────────────────────────────────────────────────────────────────────

// "2026-10-26" -> "Oct 26, 2026" (parsed as local midnight so it never drifts a day west of UTC).
export const fmtDateOnly = (value) => {
  const iso = isoDate(value);
  return iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '';
};

const fmtMonthDay = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: '2-digit' });

/** "Oct 26 – Nov 01, 2026" (same year), "Dec 29, 2026 – Jan 04, 2027" (across years), "Oct 26, 2026" (one day). */
export const formatPeriodRange = (start, end) => {
  const s = isoDate(start);
  const e = isoDate(end);
  if (!s && !e) return '';
  if (!s || !e || s === e) return fmtDateOnly(s || e);
  return s.slice(0, 4) === e.slice(0, 4) ? `${fmtMonthDay(s)} – ${fmtDateOnly(e)}` : `${fmtDateOnly(s)} – ${fmtDateOnly(e)}`;
};

// ── Phase ────────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * upcoming / live / ended for a quiz, from its Eastern dates (start 00:00:00 to end 23:59:59.9999999 Eastern, so on
 * the whole-day level: before the start date, between the dates inclusive, after the end date).
 */
export const phaseOf = (quiz, today) => {
  const start = isoDate(quiz?.startDate);
  const end = isoDate(quiz?.endDate);
  if (!start || !end || !today) return quiz?.phase ?? null;
  if (today < start) return 'upcoming';
  if (today > end) return 'ended';
  return 'live';
};

// ── The form model ───────────────────────────────────────────────────────────────────────────────────────────
//
// text is a map { [localeId]: string } while editing; the API wants { values: [{ localeId, value }] }.

let keySeed = 0;
const nextKey = () => {
  keySeed += 1;
  return `q${keySeed}`;
};

export const textToMap = (text) => {
  const values = Array.isArray(text) ? text : (Array.isArray(text?.values) ? text.values : []);
  const map = {};
  values.forEach((v) => { if (v?.localeId) map[v.localeId] = v.value ?? ''; });
  return map;
};

export const mapToValues = (map) =>
  Object.entries(map ?? {})
    .map(([localeId, value]) => ({ localeId, value: String(value ?? '').trim() }))
    .filter((v) => v.value !== '');

const hasText = (map) => mapToValues(map).length > 0;

export const newOption = () => ({ _key: nextKey(), id: null, text: {}, isCorrect: false });

export const newQuestion = (optionCount = DEFAULT_OPTION_COUNT) => ({
  _key: nextKey(),
  id: null,
  text: {},
  options: Array.from({ length: optionCount }, newOption),
});

/** An empty form: a weekly quiz for the week of `today`, 5 questions of 4 options. */
export const newForm = (today, questionCount = DEFAULT_QUESTION_COUNT) => ({
  name: '',
  scheduleType: 'weekly',
  referenceDate: today,
  startDate: '',
  endDate: '',
  questions: Array.from({ length: questionCount }, () => newQuestion()),
});

const bySortOrder = (a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0);

/**
 * A quiz — the API detail or an approval snapshot (same shape) — as a form. Rows keep their server ids, so a save
 * matches them instead of recreating them. A Weekly/Monthly quiz uses its start date as the reference date, which
 * recomputes exactly the same period.
 */
export const quizToForm = (quiz) => {
  const type = String(quiz?.scheduleType ?? 'custom').toLowerCase();
  const start = isoDate(quiz?.startDate);
  return {
    name: quiz?.name ?? '',
    scheduleType: type,
    referenceDate: type === 'custom' ? '' : start,
    startDate: start,
    endDate: isoDate(quiz?.endDate),
    questions: [...(quiz?.questions ?? [])].sort(bySortOrder).map((q) => ({
      _key: nextKey(),
      id: q.id ?? null,
      text: textToMap(q.text),
      options: [...(q.options ?? [])].sort(bySortOrder).map((o) => ({
        _key: nextKey(),
        id: o.id ?? null,
        text: textToMap(o.text),
        isCorrect: Boolean(o.isCorrect),
      })),
    })),
  };
};

/** The save request (POST / PUT /cms/quiz): ids only for rows that exist, no blank translations, no `key`s. */
export const buildSavePayload = (form) => {
  const custom = form.scheduleType === 'custom';
  return {
    name: String(form.name ?? '').trim(),
    scheduleType: form.scheduleType,
    ...(custom ? { startDate: form.startDate, endDate: form.endDate } : { referenceDate: form.referenceDate }),
    questions: form.questions.map((q) => ({
      ...(q.id != null ? { id: q.id } : {}),
      text: { values: mapToValues(q.text) },
      options: q.options.map((o) => ({
        ...(o.id != null ? { id: o.id } : {}),
        text: { values: mapToValues(o.text) },
        isCorrect: Boolean(o.isCorrect),
      })),
    })),
  };
};

// ── Validation (plan 9.2.4) ──────────────────────────────────────────────────────────────────────────────────

/** What is missing in one question: [] when it is complete. */
export const questionIssues = (question) => {
  const issues = [];
  if (!hasText(question.text)) issues.push('Question text is missing');

  const options = question.options ?? [];
  if (options.length < MIN_OPTIONS) issues.push(`Add at least ${MIN_OPTIONS} options`);
  else if (options.some((o) => !hasText(o.text))) issues.push('Every option needs text');

  const correct = options.filter((o) => o.isCorrect).length;
  if (correct === 0) issues.push('No correct answer');
  if (correct > 1) issues.push('Only one answer can be correct');
  return issues;
};

/**
 * Every rule a quiz must pass before it can be saved. `today` is the Eastern date; `siblings` the other quizzes of
 * the space (toSiblingRanges). The picked date (the start date for Custom, the reference date for Weekly/Monthly)
 * can't be in the past, today is fine.
 */
export const validateForm = (form, { today, siblings = [] }) => {
  const name = String(form.name ?? '').trim();
  const custom = form.scheduleType === 'custom';

  const nameError = name.length === 0 ? 'Name is required' : (name.length > NAME_MAX_LENGTH ? `At most ${NAME_MAX_LENGTH} characters` : null);

  const period = custom ? { start: form.startDate, end: form.endDate } : resolvePeriod(form.scheduleType, form.referenceDate);
  const picked = custom ? form.startDate : form.referenceDate;

  let dateError = null;
  if (custom && (!form.startDate || !form.endDate)) dateError = 'Pick a start and an end date';
  else if (custom && form.startDate > form.endDate) dateError = 'Start date must be on or before the end date';
  else if (!custom && !form.referenceDate) dateError = 'Pick a date';
  else if (picked && today && picked < today) dateError = "The date can't be in the past";

  const overlap = dateError ? null : findOverlap(period?.start, period?.end, siblings);

  const questions = form.questions ?? [];
  const questionsError = questions.length === 0 ? 'Add at least one question' : null;
  const perQuestion = questions.map(questionIssues);

  const valid = !nameError && !dateError && !overlap && !questionsError && perQuestion.every((i) => i.length === 0);
  return { valid, nameError, dateError, overlap, questionsError, perQuestion, period };
};

// ── Table rows ───────────────────────────────────────────────────────────────────────────────────────────────

/** What a row of the Game Schedule table is, from the approval state machine (plan 13.2). */
export const rowState = (row) => {
  const snapshot = row?._viewType === 'snapshot';
  const approvalStatus = row?.approvalStatus ?? null;
  const contentStatus = row?.contentStatus ?? 'draft';
  const hasProposal = approvalStatus === 'pending' || approvalStatus === 'rejected';

  if (snapshot) return approvalStatus === 'rejected' ? 'rejected-proposal' : 'pending-proposal';
  if (contentStatus === 'draft') {
    if (!row?.approvalId) return 'draft';
    return approvalStatus === 'pending' ? 'pending-create' : 'rejected-create';
  }
  // A proposal wins over the lock: a started quiz can only be changed through one, so while it is open the row says so.
  if (hasProposal) return 'live-with-proposal';
  return row?.isLocked ? 'live-locked' : 'live';
};
